import { defineStore } from 'pinia';
import { EOverlayItemType, useOverlayQueueStore } from '../abstract/overlayQueue';
import type { EInGameMenuName } from '~/types/inGameMenu';

export enum DropdownMenuEnum {
  providersList = 'providersList',
  gameCollections = 'gameCollections',
  notificationsMenu = 'notificationsMenu',
}

export enum LayoutMenuEnum {
  gameSearch = 'search',
  sidebar = 'sidebar',
  game = 'game',
  inGamePanel = 'ingamepanel',
}

export const useStoreMenuStatuses = defineStore('storeMenuStatuses', {
  state: () => ({
    currentInGameActiveMenu: null as EInGameMenuName | null,
    isSidebarMenuShown: false,
    isFocusOnGameMode: false,
    isGameTheaterMode: false,
    isCurrencySelectorShown: false,
    isSupportChatShown: false,
    bodyOverflowHidden: {
      status: false,
      scrollOffset: 0,
    },
    isBonusShown: true,
    isGameRealCurrencyMode: { '1': false, '2': false, '3': false, '4': false } as Record<
      string,
      boolean
    >,
    isBottomMenuShown: false,
    scrollToElement: null as null | string,
  }),
  getters: {
    statuses(): Record<DropdownMenuEnum, boolean> {
      return Object.values(DropdownMenuEnum).reduce(
        (acc, menu) => ({ ...acc, [menu]: useOverlayQueueStore().queueList.includes(menu) }),
        {} as Record<DropdownMenuEnum, boolean>
      );
    },
    isMobileGameOpen() {
      return useOverlayQueueStore().queueList.includes(LayoutMenuEnum.game);
    },
    isNotificationsMenuShown() {
      return useOverlayQueueStore().queueList.includes(DropdownMenuEnum.notificationsMenu);
    },
    isGameSearchModalShown() {
      return useOverlayQueueStore().queueList.includes(LayoutMenuEnum.gameSearch);
    },
    isInGamePanelShown() {
      return useOverlayQueueStore().queueList.includes(LayoutMenuEnum.inGamePanel);
    },
    isBodyOverflowHidden(state) {
      return state.bodyOverflowHidden.status;
    },
    scrollPageOffest(state) {
      return state.bodyOverflowHidden.scrollOffset;
    },
    focusOnGameMode(state) {
      return state.isFocusOnGameMode;
    },
    gameTheaterMode(state) {
      return state.isGameTheaterMode;
    },
    currencySelectorShown(state) {
      return state.isCurrencySelectorShown;
    },
  },
  actions: {
    openMenu(menu: DropdownMenuEnum) {
      this.closeAllMenus();
      useOverlayQueueStore().addToOverlayQueue({
        name: menu,
        type: EOverlayItemType.dropdown,
      });
    },

    closeMenu(menu: DropdownMenuEnum) {
      useOverlayQueueStore().removeFromOverlayQueue(menu);
    },

    closeAllMenus() {
      useOverlayQueueStore().removeFromOverlayQueueByType(EOverlayItemType.dropdown);
    },

    toggleMenu(menu: DropdownMenuEnum) {
      if (this.statuses[menu]) {
        this.closeMenu(menu);
      } else {
        this.openMenu(menu);
      }
    },

    /**
     * this method toggle show/hide NotificationsMenu
     * @returns true
     */
    toggleNotificationsMenu(value?: boolean) {
      if (value ?? !this.isNotificationsMenuShown) {
        this.openMenu(DropdownMenuEnum.notificationsMenu);
      } else {
        this.closeMenu(DropdownMenuEnum.notificationsMenu);
      }
    },

    toggleGameSearch(value: boolean) {
      if (value ?? !this.isGameSearchModalShown) {
        useOverlayQueueStore().addToOverlayQueue({
          name: LayoutMenuEnum.gameSearch,
          type: EOverlayItemType.modal,
          shouldBeAttchedToQuery: true,
          shouldOverlap: true,
        });
      } else {
        useOverlayQueueStore().removeFromOverlayQueue(LayoutMenuEnum.gameSearch);
      }
    },

    toggleInGamePanel(value: boolean) {
      if (value ?? !this.isGameSearchModalShown) {
        useOverlayQueueStore().addToOverlayQueue({
          name: LayoutMenuEnum.inGamePanel,
          type: EOverlayItemType.other,
          shouldBeAttchedToQuery: true,
          shouldOverlap: true,
        });
      } else {
        useOverlayQueueStore().removeFromOverlayQueue(LayoutMenuEnum.inGamePanel);
      }
    },

    toggleIsBonusShownBlock(value?: boolean) {
      this.isBonusShown = value ?? !this.isBonusShown;
    },

    /**
     * this method toggle show/hide sidemenu
     * @returns void
     */
    toggleSidebarMenu(value?: boolean) {
      this.isSidebarMenuShown = value ?? !this.isSidebarMenuShown;
    },

    /**
     * this method toggle game currency mode
     * @returns true
     */
    toggleGameCurrencyMode(screen: string, value?: boolean) {
      if (value !== undefined) {
        this.isGameRealCurrencyMode[screen] = value as boolean;
      } else {
        this.isGameRealCurrencyMode[screen] = !this.isGameRealCurrencyMode[screen];
      }
    },
    setMobileGameShowStatus(status: boolean, closeAllModals = true) {
      if (this.isMobileGameOpen === status) {
        return;
      }

      if (status) {
        useOverlayQueueStore().addToOverlayQueue({
          name: LayoutMenuEnum.game,
          type: EOverlayItemType.fullscreen,
          shouldBeAttchedToQuery: true,
          shouldOverlap: true,
        });
      } else {
        useOverlayQueueStore().removeFromOverlayQueue(LayoutMenuEnum.game);
        // @ts-ignore
        window.LiveChatWidget?.call('hide');

        if (closeAllModals) {
          useOverlayQueueStore().removeFromOverlayQueueByType(EOverlayItemType.modal);
        }

        if (this.isInGamePanelShown === true) {
          this.toggleInGamePanel(false);
        }
      }
    },
    setCurrentActiveInGameMenu(value: EInGameMenuName | null) {
      this.currentInGameActiveMenu = value;
    },
    addBodyOverflowHidden(offset = 0) {
      this.bodyOverflowHidden = {
        status: true,
        scrollOffset: offset,
      };
    },
    removeBodyOverflowHidden() {
      this.bodyOverflowHidden.status = false;
    },
    setFocusOnGameMode(val: boolean) {
      this.isFocusOnGameMode = val;
    },
    setGameTheaterMode(val: boolean) {
      this.isGameTheaterMode = val;
    },
    setCurrencySelectorVisibility(val: boolean) {
      this.isCurrencySelectorShown = val;
    },
    setSupportChatVisibility(val: boolean) {
      this.isSupportChatShown = val;
    },
    setMenuBottomVisibility(val: boolean) {
      this.isBottomMenuShown = val;
    },
    // scroll to element after navigating to page, example: #category
    setScrollToElement(val: null | string) {
      this.scrollToElement = val;
    },
  },
});
